import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '../components/ui/tooltip';
import { FormCapabilitiesSection } from './FormCapabilitiesSection';
import { FormItemPanel } from './FormItemPanel';
import { FormItemBlockList } from './FormItemBlockList';
import { captureCaret, freshRenderKey } from './items/ItemBlock';
import { inferFormItem } from '../inference/formItemInference';
/**
 * FormPanel — the top-level drawer for a Form Node.
 *
 * Authoring model: each item is a focusable block in a Slate-style editor.
 *   - Enter commits the current block (fires inference if dirty + untouched)
 *     and appends a new empty block below.
 *   - Backspace at start of an empty block deletes it + focuses prior.
 *   - Blur outside the list fires inference on the just-left block.
 *   - Drawer-touched items are permanently immune to inference overwrites.
 */
export function FormPanel({ form, knowledgeBases = [], graphKnowledgeBaseIds = [], availableScratchpadPaths = [], variables, onChange, }) {
    const [selectedItemIndex, setSelectedItemIndex] = useState(null);
    const [inferringIds, setInferringIds] = useState(() => new Set());
    const [touchedIds, setTouchedIds] = useState(() => new Set(form.items.map(i => i.id)));
    const [focusItemId, setFocusItemId] = useState(null);
    /**
     * The text the last successful inference ran against, per item id. Lets
     * us skip re-inferring when nothing changed between blurs. Mutated in
     * place because it's an identity-stable ref — no re-render needed.
     */
    const lastInferredTextRef = useRef(new Map());
    /**
     * Items with an in-flight inference request keyed by current item id.
     * Separate from the `inferringIds` React state because we need an
     * identity-stable check inside runInference to dedup Enter+blur
     * double-fires that both land before the first LLM reply.
     */
    const inFlightRef = useRef(new Set());
    /** Synced via layout effect so runInference reads the post-commit items. */
    const itemsRef = useRef(form.items);
    useLayoutEffect(() => {
        itemsRef.current = form.items;
    }, [form.items]);
    // When items change from an outside source (undo/redo, loadCanvas),
    // mark freshly-loaded ids as touched so the inference-on-mount dance
    // doesn't fire against text we already have structure for.
    const prevItemsRef = useRef(form.items);
    useEffect(() => {
        if (form.items === prevItemsRef.current)
            return;
        setTouchedIds(prev => {
            const next = new Set(prev);
            for (const item of form.items) {
                if (!prev.has(item.id))
                    next.add(item.id);
            }
            return next;
        });
        prevItemsRef.current = form.items;
    }, [form.items]);
    const setItems = useCallback((next, options) => {
        onChange({ items: next }, options);
        prevItemsRef.current = next;
    }, [onChange]);
    const updateItemText = useCallback((itemId, text) => {
        const next = form.items.map(item => item.id === itemId ? { ...item, text } : item);
        setItems(next);
    }, [form.items, setItems]);
    const markTouched = useCallback((itemId) => {
        setTouchedIds(prev => {
            if (prev.has(itemId))
                return prev;
            const next = new Set(prev);
            next.add(itemId);
            return next;
        });
    }, []);
    const handleItemPanelChange = useCallback((index, patch) => {
        const item = form.items[index];
        if (!item)
            return;
        markTouched(item.id);
        const next = form.items.map((it, i) => i === index ? { ...it, ...patch } : it);
        setItems(next);
    }, [form.items, markTouched, setItems]);
    const runInference = useCallback(async (itemId) => {
        // Read from itemsRef (not the form.items closure) so sync-fired
        // inference from handleCommitAndAppend sees the just-added fresh
        // block, not the pre-commit snapshot — otherwise the resolve-time
        // setItems overwrites and drops the fresh block from state.
        const current = itemsRef.current.find(i => i.id === itemId);
        if (!current || !current.text.trim())
            return;
        if (touchedIds.has(itemId))
            return;
        if (inFlightRef.current.has(itemId))
            return;
        const lastInferred = lastInferredTextRef.current.get(itemId);
        if (lastInferred === current.text)
            return;
        inFlightRef.current.add(itemId);
        setInferringIds(prev => {
            const next = new Set(prev);
            next.add(itemId);
            return next;
        });
        const requestText = current.text;
        try {
            const inferred = await inferFormItem({
                text: requestText,
                siblingIds: itemsRef.current
                    .map(i => i.id)
                    .filter(id => id !== itemId),
                availableScratchpadPaths,
            });
            if (!inferred)
                return;
            const latestIndex = itemsRef.current.findIndex(i => i.id === itemId);
            if (latestIndex === -1)
                return;
            const latest = itemsRef.current[latestIndex];
            if (!latest)
                return;
            if (touchedIds.has(itemId))
                return;
            // Text changed between the LLM request and its response (undo,
            // redo, or a fresh edit landed in the gap). Dropping the inferred
            // metadata here is safer than writing it onto text the author no
            // longer wrote.
            if (latest.text !== requestText)
                return;
            // Preserve the author-chosen depth across inference — nesting is
            // a UI concern, not something the LLM should decide.
            const currentDepth = latest.depth ?? 0;
            const stableRenderKey = latest.renderKey ?? freshRenderKey();
            const nextItem = inferred.kind === 'prompt'
                ? {
                    kind: 'prompt',
                    id: inferred.id,
                    text: latest.text,
                    mode: inferred.mode ?? 'verbatim',
                    depth: currentDepth,
                    renderKey: stableRenderKey,
                }
                : {
                    kind: 'question',
                    id: inferred.id,
                    text: latest.text,
                    mode: inferred.mode ?? 'verbatim',
                    required: inferred.required ?? false,
                    answerType: inferred.answerType,
                    templateVars: inferred.templateVars ?? undefined,
                    scratchpadMapping: inferred.scratchpadPath
                        ? {
                            path: inferred.scratchpadPath,
                            direction: 'read_write',
                            renderPolicy: 'auto',
                        }
                        : undefined,
                    depth: currentDepth,
                    renderKey: stableRenderKey,
                };
            const nextItems = itemsRef.current.map((it, i) => i === latestIndex ? nextItem : it);
            const restoreCaret = captureCaret();
            // Inference writes metadata the author didn't type. Skip history so
            // an Undo back past the just-typed text doesn't land on a halfway
            // "text present, metadata stripped" state.
            setItems(nextItems, { skipHistory: true });
            restoreCaret();
            // Carry the lastInferredText + inferring flag over to the new id if
            // inference renamed the item.
            if (nextItem.id !== itemId) {
                lastInferredTextRef.current.delete(itemId);
            }
            lastInferredTextRef.current.set(nextItem.id, nextItem.text);
        }
        finally {
            inFlightRef.current.delete(itemId);
            setInferringIds(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
        }
    }, [form.items, touchedIds, availableScratchpadPaths, setItems]);
    const allocateId = useCallback((seed) => {
        const used = new Set(seed.map(i => i.id));
        for (let n = used.size + 1; n < used.size + 500; n++) {
            const candidate = `item_${n}`;
            if (!used.has(candidate))
                return candidate;
        }
        return `item_${Date.now()}`;
    }, []);
    const newPlaceholderItem = useCallback((seed, text = '') => ({
        kind: 'question',
        id: allocateId(seed),
        text,
        mode: 'verbatim',
        required: false,
        answerType: { kind: 'string' },
        depth: 0,
        renderKey: freshRenderKey(),
    }), [allocateId]);
    const handleCommitAndAppend = useCallback((afterItemId) => {
        const beforeIndex = form.items.findIndex(i => i.id === afterItemId);
        if (beforeIndex === -1)
            return;
        const fresh = newPlaceholderItem(form.items);
        const nextItems = [
            ...form.items.slice(0, beforeIndex + 1),
            fresh,
            ...form.items.slice(beforeIndex + 1),
        ];
        setItems(nextItems);
        setFocusItemId(fresh.id);
        // Fire inference on the committed block.
        void runInference(afterItemId);
    }, [form.items, newPlaceholderItem, setItems, runInference]);
    const handleAppendItem = useCallback(() => {
        const fresh = newPlaceholderItem(form.items);
        setItems([...form.items, fresh]);
        setFocusItemId(fresh.id);
        // Inference on the previously-last block, now that it is committed.
        const lastId = form.items[form.items.length - 1]?.id;
        if (lastId)
            void runInference(lastId);
    }, [form.items, newPlaceholderItem, setItems, runInference]);
    const handleDeleteEmpty = useCallback((itemId) => {
        const index = form.items.findIndex(i => i.id === itemId);
        if (index === -1)
            return;
        if (form.items.length === 1)
            return; // keep at least one block
        const nextItems = form.items.filter(i => i.id !== itemId);
        setItems(nextItems);
        const priorId = nextItems[Math.max(0, index - 1)]?.id ?? nextItems[0]?.id ?? null;
        setFocusItemId(priorId);
        setInferringIds(prev => {
            if (!prev.has(itemId))
                return prev;
            const next = new Set(prev);
            next.delete(itemId);
            return next;
        });
        lastInferredTextRef.current.delete(itemId);
        setTouchedIds(prev => {
            if (!prev.has(itemId))
                return prev;
            const next = new Set(prev);
            next.delete(itemId);
            return next;
        });
    }, [form.items, setItems]);
    const handlePasteSplit = useCallback((atItemId, lines) => {
        const atIndex = form.items.findIndex(i => i.id === atItemId);
        if (atIndex === -1 || lines.length === 0)
            return;
        // Replace the item at atIndex with lines[0], then insert the rest.
        const seed = [...form.items];
        const base = seed[atIndex];
        if (!base)
            return;
        const first = { ...base, text: lines[0] ?? '' };
        const inserts = [];
        let working = [...seed.slice(0, atIndex), first];
        for (let n = 1; n < lines.length; n++) {
            const fresh = newPlaceholderItem([...working, ...inserts, ...seed.slice(atIndex + 1)], lines[n]);
            inserts.push(fresh);
        }
        const nextItems = [
            ...seed.slice(0, atIndex),
            first,
            ...inserts,
            ...seed.slice(atIndex + 1),
        ];
        setItems(nextItems);
        // Fire inference on each updated/new line (except the ones already
        // locked by the drawer).
        void runInference(first.id);
        for (const inserted of inserts) {
            void runInference(inserted.id);
        }
    }, [form.items, newPlaceholderItem, setItems, runInference]);
    // Ensure there is always at least one block so the editor is never empty.
    // Seeded with skipHistory so Undo back to the just-added form doesn't
    // bounce off the seed and loop.
    useEffect(() => {
        if (form.items.length === 0) {
            const seed = newPlaceholderItem([]);
            setItems([seed], { skipHistory: true });
            setFocusItemId(seed.id);
        }
    }, [form.items.length, newPlaceholderItem, setItems]);
    const handleFocusConsumed = useCallback(() => setFocusItemId(null), []);
    const handleIndent = useCallback((itemId) => {
        const index = form.items.findIndex(i => i.id === itemId);
        if (index === -1)
            return;
        // Can't indent the first item (no parent to nest under).
        if (index === 0)
            return;
        const item = form.items[index];
        if (!item || (item.depth ?? 0) === 1)
            return;
        const next = form.items.map((it, i) => i === index ? { ...it, depth: 1 } : it);
        setItems(next);
    }, [form.items, setItems]);
    const handleOutdent = useCallback((itemId) => {
        const index = form.items.findIndex(i => i.id === itemId);
        if (index === -1)
            return;
        const item = form.items[index];
        if (!item || (item.depth ?? 0) === 0)
            return;
        const next = form.items.map((it, i) => i === index ? { ...it, depth: 0 } : it);
        setItems(next);
    }, [form.items, setItems]);
    const handleFocusPrev = useCallback((itemId) => {
        const index = form.items.findIndex(i => i.id === itemId);
        if (index <= 0)
            return;
        const priorId = form.items[index - 1]?.id;
        if (priorId)
            setFocusItemId(priorId);
    }, [form.items]);
    const handleFocusNext = useCallback((itemId) => {
        const index = form.items.findIndex(i => i.id === itemId);
        if (index < 0)
            return;
        const nextId = form.items[index + 1]?.id;
        if (nextId)
            setFocusItemId(nextId);
    }, [form.items]);
    if (selectedItemIndex !== null && form.items[selectedItemIndex]) {
        return (_jsx(FormItemPanel, { item: form.items[selectedItemIndex], index: selectedItemIndex, availableScratchpadPaths: availableScratchpadPaths, inferring: inferringIds.has(form.items[selectedItemIndex].id), onBack: () => setSelectedItemIndex(null), onChange: patch => handleItemPanelChange(selectedItemIndex, patch) }));
    }
    const anyInferring = inferringIds.size > 0;
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Title" }), _jsx(Input, { value: form.title, onChange: e => onChange({ title: e.target.value }), placeholder: "Owner lead discovery", className: "text-xs h-8" })] }), _jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Label, { className: "text-xs", children: "Questions" }), _jsx(TooltipProvider, { children: _jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx(Info, { className: "inline-block h-4 w-4 text-muted-foreground cursor-help ml-1.5 -mt-0.5 align-middle", "aria-label": "More information" }) }), _jsx(TooltipContent, { side: "bottom", className: "max-w-[220px] whitespace-wrap", children: _jsx("span", { className: "text-xs text-left w-full block relative", children: "Press Enter to start the next line, Tab to indent as a sub-question, and Shift+Tab to outdent or focus the previous line." }) })] }) })] }), anyInferring && (_jsxs("span", { className: "inline-flex items-center gap-1 text-[10px] text-muted-foreground", children: [_jsx(Loader2, { className: "h-3 w-3 animate-spin" }), "inferring\u2026"] }))] }), _jsx(FormItemBlockList, { items: form.items, inferringIds: inferringIds, touchedIds: touchedIds, variables: variables, onTextChange: updateItemText, onCommitAndAppend: handleCommitAndAppend, onAppendItem: handleAppendItem, onDeleteEmpty: handleDeleteEmpty, onPasteSplit: handlePasteSplit, onIndent: handleIndent, onOutdent: handleOutdent, onFocusPrev: handleFocusPrev, onFocusNext: handleFocusNext, onBlurBlock: runInference, onOpenItem: id => {
                            const idx = form.items.findIndex(i => i.id === id);
                            if (idx !== -1)
                                setSelectedItemIndex(idx);
                        }, focusItemId: focusItemId, onFocusConsumed: handleFocusConsumed })] }), _jsx(FormCapabilitiesSection, { form: form, knowledgeBases: knowledgeBases, graphKnowledgeBaseIds: graphKnowledgeBaseIds, onChange: patch => onChange(patch) })] }));
}
//# sourceMappingURL=FormPanel.js.map